/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { FeatureFlags } from '@constants/featureFlags';
import {
  AddressBookModel,
  fetchAddressBookById,
} from '@store/customers/customerDetails/addressBookTab';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { getLTLFalveyPricingQuotes } from '@store/ltl/actions';
import {
  fetchQuoteById,
  fetchQuoteParametersByQuoteId,
  getDefaultAddress,
  getTempQuote,
} from '@store/ltl/services';
import { setLTLQuote } from '@store/ltl/store';
import {
  FalveyInsuranceQuoteInLoad,
  LTLAddressBookSnapshot,
  LTLHazmatDetails,
  LTLLoadItem,
  LTLPricingRequestType,
  LTLQuoteLoadItemBreakdown,
  LTLRateQuoteRequest,
  LTLRateQuoteResponse,
  QuoteContact,
  QuoteSpecifics,
  TempQuoteModel,
} from '@store/ltl/types';
import { fetchLtlQuoteLoadItems } from '@store/orders/services/ltlQuoteServices';
import {
  orderStore,
  setOrderLoading,
  setOrderStore,
} from '@store/orders/store';
import {
  FalveyInsurance,
  FalveyInsuranceQuote,
  LtlQuoteLineItemViewModel,
  LtlQuoteLoadItemViewModel,
  LtlQuoteParametersViewModel,
  LtlQuoteViewModel,
  LtlQuoteViewModelLegacy,
  LtlTerminalInfoViewModel,
} from '@store/orders/types';
import { Address } from '@typeDefinitions/addressTypes';
import { LineItemViewModel } from '@typeDefinitions/lineItemTypes';
import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import { mergeOrderQuote } from '@utils/ltl';
import { nestLoadItems } from '@utils/ltl/quoteLoadItems';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';

import {
  addCustomerPayLineItem,
  addVendorPayLineItem,
  buildLoadFromQuote,
  updateCustomerAndVendorPay,
  updateVendorPayLineItem,
} from '../helper';
import { getCarrierDetail } from './carrierActions';
import { loadCustomerInfo } from './orderCustomerActions';
import { updateOrderState } from './orderFormActions';

async function GetQuoteInfo(quote: TempQuoteModel) {
  if (quote.quoteSpecifics.requestDetails.customerId !== null) {
    if (quote.quoteSpecifics.requestDetails.customerId !== 0) {
      await loadCustomerInfo(quote.quoteSpecifics.requestDetails.customerId);
    }
  }
  await getCarrierDetail(
    Number(quote.quoteSpecifics.quoteResponse.carrierId),
    0,
  );
}

const addressBookModelToSnapshot = (
  model: AddressBookModel,
): LTLAddressBookSnapshot => ({
  addressLine1: model.addressLine1,
  addressLine2: model.addressLine2,
  locationName: model.name,
  contactName: model.contactName,
  contactPhone: model.contactPhone,
  contactEmail: model.contactEmail,
  specialInstructions: model.specialInstructions,
  internalNote: model.internalNote,
  drivingDirections: model.drivingDirections,
  openTimeFrom: model.openTimeFrom,
  openTimeTo: model.openTimeTo,
  city: model.city,
  state: model.state,
  zip: model.zip,
  countryCode: model.countryCode,
});

const defaultAddressToSnapshot = (
  address: Address,
): LTLAddressBookSnapshot => ({
  addressLine1: address.addressLine1,
  addressLine2: address.Line2,
  locationName: address.name ?? '',
  contactName: address.contactName,
  contactPhone: address.contactPhone ?? '',
  contactEmail: null,
  specialInstructions: address.specialInstructions,
  internalNote: null,
  drivingDirections: null,
  openTimeFrom: address.openTimeFrom ?? '',
  openTimeTo: address.openTimeTo ?? '',
  city: address.city,
  state: address.state,
  zip: address.zip,
  countryCode: address.countryCode,
});

const checkAddressMismatch = (
  model: AddressBookModel | undefined,
  quotedCity: string | undefined,
  quotedState: string | undefined,
  quotedZip: string | undefined,
): boolean => {
  if (!model) return false;
  const n = (s: string | null | undefined) => (s ?? '').toLowerCase().trim();
  return (
    n(model.city) !== n(quotedCity) ||
    n(model.state) !== n(quotedState) ||
    n(model.zip) !== n(quotedZip)
  );
};

export const populateOrderWithQuoteLegacy = async (id: string) => {
  setOrderLoading(true);
  const quote = (await getTempQuote(id)) as TempQuoteModel;
  await GetQuoteInfo(quote);
  const customerId = quote.ltlQuote?.rateRequest?.customerId ?? null;
  const address =
    customerId !== null
      ? ((await getDefaultAddress(customerId)) as unknown as Address)
      : undefined;
  const order =
    quote.quoteSpecifics.requestDetails.originZip === address?.zip
      ? buildLoadFromQuote(quote, address)
      : buildLoadFromQuote(quote);
  setLTLQuote({
    ltlQuote: quote.ltlQuote,
    originZip: quote.quoteSpecifics.requestDetails.originZip,
    originCity: quote.quoteSpecifics.requestDetails.originCity,
    originState: quote.quoteSpecifics.requestDetails.originState,
    originCountry: quote.quoteSpecifics.requestDetails.originCountry,
    destinationZip: quote.quoteSpecifics.requestDetails.destinationZip,
    destinationCity: quote.quoteSpecifics.requestDetails.destinationCity,
    destinationState: quote.quoteSpecifics.requestDetails.destinationState,
    destinationCountry: quote.quoteSpecifics.requestDetails.destinationCountry,
    customerId: quote.quoteSpecifics.requestDetails.customerId ?? undefined,
    customerName: quote.quoteSpecifics.requestDetails.customerName ?? undefined,
    pickupDate: quote.quoteSpecifics.requestDetails.pickupDate,
    loadItems: quote.quoteSpecifics.requestDetails.loadItems,
  });
  //Note: setting the bid type to Spot Market if it is null so creating an order
  //from a saved quote never leaves the required Bid Type field unset.
  updateOrderState({
    ...order,
    bidType: Boolean(orderStore.order.bidType)
      ? orderStore.order.bidType
      : 'Spot Market',
  });
  if (
    quote.ltlQuote.ltlInsurance &&
    quote.ltlQuote.ltlInsurance.shippingSumInsured > 0
  ) {
    const { shippingSumInsured, quoteServiceFee, quotePremium } =
      quote.ltlQuote.ltlInsurance;
    updateCustomerAndVendorPay(
      {
        shippingSumInsured,
        quotePremium,
        quoteServiceFee,
      } as FalveyInsuranceQuoteInLoad,
      'insert',
      shippingSumInsured,
    );
  }
  setOrderLoading(false);
};

// ******************************************************************************
// This block of code between these comments is going away once we remove
// TempQuote
// https://armstrong.atlassian.net/browse/VNEXT-5326
// While this new logic is behind a feature flag the easiest thing to do
// was to cohherce the new data into the old shape.
// ↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓
// ******************************************************************************
const parseLTLPricingRequestType = (
  text?: string,
): LTLPricingRequestType | null => {
  if (text === undefined) return null;

  const normalizedText = text.trim().toLowerCase();

  switch (normalizedText) {
    case 'contract':
      return LTLPricingRequestType.Contract;
    case 'dynamic':
      return LTLPricingRequestType.Dynamic;
    case 'volume':
      return LTLPricingRequestType.Volume;
    case 'none':
    default:
      return LTLPricingRequestType.None;
  }
};

const mapLineItems = (
  isCustomer: boolean,
  lineItems?: LtlQuoteLineItemViewModel[],
): LineItemViewModel[] =>
  (lineItems ?? []).map((lineItem) => ({
    id: lineItem.id,
    orderId: undefined,
    pickUpId: undefined,
    dropOffId: undefined,
    stopId: undefined,
    operationType: lineItem.operationType,
    type: lineItem.type,
    description: lineItem.description,
    descriptionWithParanthesis: undefined,
    settledDate: null,
    createdDate: undefined,
    status: undefined,
    quantity: lineItem.quantity,
    rate: isCustomer ? lineItem.customerRate : lineItem.carrierRate,
    estimate: false,
    readOnly: false,
    synched: false,
    adjustment: false,
    referenceNumber: undefined,
    rateDetail: undefined,
  }));

export const mapLoadItems = (
  loadItems?: LtlQuoteLoadItemViewModel[],
): LTLLoadItem[] =>
  (loadItems ?? []).map((loadItem, index) => ({
    charge: (loadItem.charge ?? '').toString(),
    class: loadItem.class ?? null,
    cubicFeet: loadItem.volume ?? undefined,
    description: loadItem.description ?? '',
    hazmat: loadItem.hazmat,
    hazmatDescription: loadItem.hazmatDescription ?? null,
    hazmatItems: loadItem.hazmatItems ?? null,
    height: null,
    heightInch: loadItem.height ?? null,
    index,
    item: loadItem.unitType,
    length: null,
    lengthInch: loadItem.length ?? null,
    ltlQuoteLoadItemId:
      loadItem.id === undefined || loadItem.id === null
        ? undefined
        : String(loadItem.id),
    loadItemHazmatDetail: loadItem.quoteHazmatDetail ?? null,
    nmfc: loadItem.nmfc ?? null, // I promise this is the correct mapping
    pieces: (loadItem.pieces ?? '').toString(),
    quantity: Number(loadItem.quantity) || 0,
    suggestedClass: loadItem.suggestedClass ?? null,
    type: loadItem.type ?? undefined,
    uom: 'lb(s)',
    volume: loadItem.volume,
    weight: loadItem.weight ?? null,
    width: null,
    widthInch: loadItem.width ?? null,
    quoteLoadItemBreakdown: (loadItem.quoteLoadItemBreakdown ?? []).map(
      (breakdown) => {
        return {
          ...breakdown,
          ltlQuoteLoadItemId:
            breakdown.id === undefined || breakdown.id === null
              ? undefined
              : String(breakdown.id),
          pieces:
            breakdown.pieces === undefined || breakdown.pieces === null
              ? null
              : String(breakdown.pieces),
          loadItemHazmatDetail: breakdown.quoteHazmatDetail
            ? ({ ...breakdown.quoteHazmatDetail } as LTLHazmatDetails)
            : undefined,
        } as LTLQuoteLoadItemBreakdown;
      },
    ),
  }));

const createCustomerRateQuote = (
  quoteParameters: LtlQuoteParametersViewModel,
  quote: LtlQuoteViewModel,
): LTLRateQuoteResponse =>
  ({
    carrierId: quote.carrierId ?? '',
    carrierSCAC: 'SMC3', // This is disgusting. We have a story for removing Tempquote and this will go away
    destinationZip: quoteParameters.destinationZip,
    estimatedDeliveryDate: quote.estimatedDeliveryDate,
    internalSCAC: quote.scac,
    lineItems: mapLineItems(true, quote.lineItems),
    maxNewLiability: quote.maxNewLiability,
    maxUsedLiability: quote.maxUsedLiability,
    movementType: quote.movementType,
    netFreightCost: quote.carrierTotal,
    originZip: quoteParameters.originZip,
    quoteID: quote.carrierQuoteId ?? '',
    quoteRequestLocator: quoteParameters.quoteRequestLocator,
    serviceType: quote.serviceTypeDisplayName,
    shipper: quote.carrierName,
    terminalInfo: quote.terminalInfo,
    totalCost: quote.customerTotal,
    transitDays: quote.transitDays,
  }) as LTLRateQuoteResponse;

function createEstimatedDeliveryDate(
  quoteParameters: LtlQuoteParametersViewModel,
  quote: LtlQuoteViewModel,
): string {
  if (
    quote.estimatedDeliveryDate === '0001-01-01T00:00:00' ||
    quote.estimatedDeliveryDate === undefined
  ) {
    return new Date(
      (quoteParameters.pickupDate != null
        ? new Date(quoteParameters.pickupDate)
        : new Date()
      ).getTime() +
        (Number(quote.transitDays) || 0) * 86_400_000, // 24 * 60 * 60 * 1000
    )
      .toISOString()
      .split('T')[0];
  }
  return quote.estimatedDeliveryDate;
}

function createQuoteResponse(
  quoteParameters: LtlQuoteParametersViewModel,
  quote: LtlQuoteViewModel,
): LTLRateQuoteResponse {
  return {
    baseTariffEffectiveDate: '',
    baseTariffName: '',
    carrierId: quote.carrierId?.toString() ?? '',
    carrierSCAC: 'SMC3', // This is disgusting. We have a story for removing Tempquote and this will go away
    costPlus: 0,
    costPlusId: null,
    costPlusOverrideId: null,
    currencyCode: '',
    customerRateQuote: createCustomerRateQuote(quoteParameters, quote),
    deliveryTimestamp: null,
    destinationContact: {} as QuoteContact,
    destinationZip: quoteParameters.destinationZip,
    estimatedDeliveryDate: createEstimatedDeliveryDate(quoteParameters, quote),
    errors: [],
    exception: '',
    id: quote.carrierId ?? null,
    internalSCAC: quote.scac ?? '',
    isCustomer: false,
    isDirectQuote: false,
    isSaved: false,
    lineItems: mapLineItems(false, quote.lineItems),
    lTLQuoteId: quote.ltlQuoteId ?? null,
    maxNewLiability: quote.maxNewLiability ?? null,
    maxUsedLiability: quote.maxUsedLiability ?? null,
    movementType: quote.movementType ?? '',
    netFreightCost: quote.carrierTotal,
    note: '',
    originContact: {} as QuoteContact,
    originZip: quoteParameters.originZip,
    parentProvider: quoteParameters.provider,
    pricingRequestType: parseLTLPricingRequestType(quote.pricingRequestType),
    quoteDateTimeUtc: quote.quoteDate || null,
    quoteHash: '',
    quoteID: quote.carrierQuoteId ?? '',
    quoteParametersId: quoteParameters.id,
    quoteRequestId: quote.ltlQuoteId ?? '',
    quoteRequestLocator: quoteParameters.quoteRequestLocator,
    serviceType: quote.serviceTypeDisplayName ?? '',
    shipper: quote.carrierName ?? '',
    tariffDescription: '',
    terminalInfo: quote.terminalInfo || ({} as LtlTerminalInfoViewModel),
    terminalLink: '',
    totalCost: quote.customerTotal ?? quote.carrierTotal,
    transactionId: quote.providerTransactionId ?? '',
    transitDays: quote.transitDays ?? '',
    useCarrier: false,
  };
}

const createRequestDetails = (
  quoteParameters: LtlQuoteParametersViewModel,
): LTLRateQuoteRequest => ({
  accessorials: [],
  correlationId: null,
  customerId: quoteParameters.customerId ?? null,
  customerName: quoteParameters.customerName ?? null,
  declaredValue: undefined,
  destinationCity: quoteParameters.destinationCity,
  destinationCountry: quoteParameters.destinationCountry,
  destinationState: quoteParameters.destinationState,
  destinationZip: quoteParameters.destinationZip,
  enableCSP: false,
  hasAccessorials: Boolean(
    quoteParameters.pickupAccessorials?.length ??
    quoteParameters.deliveryAccessorials?.length ??
    quoteParameters.shipmentAccessorials?.length,
  ),
  isLinearFeetOverridden: quoteParameters.isLinearFeetOverridden,
  isStackable: quoteParameters.isStackable,
  linearFeet: quoteParameters.linearFeet.toString(),
  loadItems: mapLoadItems(quoteParameters.loadItems),
  originCity: quoteParameters.originCity,
  originCountry: quoteParameters.originCountry,
  originState: quoteParameters.originState,
  originZip: quoteParameters.originZip,
  pcf: undefined,
  pickupDate: quoteParameters.pickupDate ?? '',
  premiumFee: undefined,
  serviceFee: undefined,
  totalCubicFeet: undefined,
});

const createLtlInsurance = (
  insuranceQuote: FalveyInsuranceQuote,
): FalveyInsurance => ({
  id: undefined,
  shipmentUUID: '',
  ltlQuoteId: '',
  loadId: undefined,
  falveyShipmentID: '',
  shippingSumInsured: insuranceQuote.shippingSumInsured,
  shippingCurrency: 'USD',
  quotePremium: insuranceQuote.premium,
  quoteServiceFee: insuranceQuote.serviceFee,
  certificateNumber: '',
  policyNumber: '',
  waybillAdditional: '',
  certificatePDFLink: '',
  createdDate: insuranceQuote.createdDate,
  modifiedDate: insuranceQuote.modifiedDate,
  createdBy: insuranceQuote.createdBy,
  modifiedBy: insuranceQuote.modifiedBy,
});

export const createLtlQuoteLegacy = (
  quoteParameters: LtlQuoteParametersViewModel,
  quote: LtlQuoteViewModel,
): LtlQuoteViewModelLegacy => ({
  carrierName: quote.carrierName,
  carrierTotal: quote.carrierTotal,
  customerId: quoteParameters.customerId,
  customerName: quoteParameters.customerName,
  customerTotal: quote.customerTotal,
  deliveryAccessorialDescription: quoteParameters.accessorials
    .filter(
      (accessorialOptions) =>
        accessorialOptions.accessorialDescription === 'Delivery',
    )
    .map((accessorial) => accessorial.accessorialName)
    .join(', '),
  deliveryAccessorials: quoteParameters.accessorials
    .filter(
      (accessorialOptions) =>
        accessorialOptions.accessorialDescription === 'Delivery',
    )

    .map((accessorial) => accessorial.accessorialName),
  id: undefined,
  ltlInsurance: quoteParameters.ltlInsuranceQuote
    ? createLtlInsurance(quoteParameters.ltlInsuranceQuote)
    : null,
  ltlQuoteLoadItems: quoteParameters.loadItems?.map(
    (loadItem) =>
      ({
        ...loadItem,
        item: loadItem.unitType,
        id: undefined,
        createdDate: undefined,
        loadItemHazmatDetail: loadItem.quoteHazmatDetail,
      }) as LtlQuoteLoadItemViewModel,
  ),
  maxNewLiability: quote.maxNewLiability,
  maxUsedLiability: quote.maxUsedLiability,
  movementType: quote.movementType,
  operationType: quote.operationType,
  pickupAccessorialDescription: quoteParameters.accessorials
    .filter(
      (accessorialOptions) =>
        accessorialOptions.accessorialDescription === 'Pickup',
    )
    .map((accessorial) => accessorial.accessorialName)
    .join(', '),
  pickupAccessorials: quoteParameters.accessorials
    .filter(
      (accessorialOptions) =>
        accessorialOptions.accessorialDescription === 'Pickup',
    )
    .map((accessorial) => accessorial.accessorialName),
  quoteDate: quote.quoteDate,
  quoteId: quote.carrierQuoteId,
  quoteParametersId: quoteParameters.id,
  rateQuote: {
    carrierId: quote.carrierId ?? '',
    carrierSCAC: 'SMC3', // This is disgusting. We have a story for removing Tempquote and this will go away
    customerRateQuote: {
      carrierId: quote.carrierId ?? '',
      carrierSCAC: 'SMC3', // This is disgusting. We have a story for removing Tempquote and this will go away
      destinationZip: quoteParameters.destinationZip,
      estimatedDeliveryDate: quote.estimatedDeliveryDate,
      internalSCAC: quote.scac,
      lineItems: mapLineItems(true, quote.lineItems),
      maxNewLiability: quote.maxNewLiability,
      maxUsedLiability: quote.maxUsedLiability,
      movementType: quote.movementType,
      netFreightCost: quote.carrierTotal,
      originZip: quoteParameters.originZip,
      quoteID: quote.carrierQuoteId ?? '',
      quoteRequestLocator: quoteParameters.quoteRequestLocator,
      serviceType: quote.serviceTypeDisplayName,
      shipper: quote.carrierName,
      terminalInfo: quote.terminalInfo,
      totalCost: quote.customerTotal,
      transitDays: quote.transitDays,
    } as LTLRateQuoteResponse,
    destinationZip: quoteParameters.destinationZip,
    estimatedDeliveryDate: quote.estimatedDeliveryDate,
    internalSCAC: quote.scac,
    lineItems: mapLineItems(false, quote.lineItems),
    maxNewLiability: quote.maxNewLiability,
    maxUsedLiability: quote.maxUsedLiability,
    movementType: quote.movementType,
    netFreightCost: quote.carrierTotal,
    originZip: quoteParameters.originZip,
    parentProvider: 'ATG', // Again this is disgusting. We have a story for removing Tempquote and this will go away
    quoteDateTimeUtc: quote.quoteDate,
    quoteID: quote.carrierQuoteId ?? '',
    quoteRequestLocator: quoteParameters.quoteRequestLocator,
    serviceType: quote.serviceTypeDisplayName,
    shipper: quote.carrierName,
    terminalInfo: quote.terminalInfo,
    totalCost: quote.customerTotal,
    transitDays: quote.transitDays,
  } as LTLRateQuoteResponse,
  rateRequest: {
    //    accessorials: quoteParameters.shipmentAccessorials ?? [],
    customerId: quoteParameters.customerId,
    customerName: quoteParameters.customerName,
    declaredValue: quoteParameters.ltlInsuranceQuote?.declaredValue,
    destinationCity: quoteParameters.destinationCity,
    destinationCountry: quoteParameters.destinationCountry,
    destinationState: quoteParameters.destinationState,
    destinationZip: quoteParameters.destinationZip,
    hasAccessorials: quoteParameters.accessorials.length > 0,
    isLinearFeetOverridden: quoteParameters.isLinearFeetOverridden,
    isStackable: quoteParameters.isStackable,
    linearFeet: quoteParameters.linearFeet.toString(),
    loadItems: mapLoadItems(quoteParameters.loadItems),
    originCity: quoteParameters.originCity,
    originCountry: quoteParameters.originCountry,
    originState: quoteParameters.originState,
    originZip: quoteParameters.originZip,
    pickupDate: quoteParameters.pickupDate,
  } as LTLRateQuoteRequest,
  serviceType: quote.serviceTypeDisplayName,
  shipmentAccessorials: quoteParameters.accessorials
    .filter((accessorialOptions) => accessorialOptions.isGlobal)
    .map((accessorial) => accessorial.accessorialName),
  terminalInfo: quote.terminalInfo,
  transitDays: quote.transitDays,
});

const createTempQuote = (
  quoteParameters: LtlQuoteParametersViewModel,
  quote: LtlQuoteViewModel,
): TempQuoteModel => {
  const quoteResponse = createQuoteResponse(quoteParameters, quote);
  const requestDetails = createRequestDetails(quoteParameters);

  const quoteSpecifics: QuoteSpecifics = {
    customerId: quote.customerId,
    quoteResponse,
    requestDetails,
  };

  const ltlQuote = createLtlQuoteLegacy(quoteParameters, quote);

  return {
    quoteSpecifics,
    ltlQuote,
    ltlQuoteId:
      quote.ltlQuoteId === undefined ? null : parseInt(quote.ltlQuoteId),
  };
};

export const populateOrderWithQuote = async (id: number) => {
  setOrderLoading(true);
  const quoteParameters = await fetchQuoteParametersByQuoteId(id, true);
  const quote = await fetchQuoteById(id, true);
  if (quote === null) {
    setOrderLoading(false);
    return;
  }
  const tempQuote = createTempQuote(quoteParameters, quote);
  await GetQuoteInfo(tempQuote);
  const address =
    quoteParameters.customerId != null
      ? ((await getDefaultAddress(
          quoteParameters.customerId,
        )) as unknown as Address)
      : undefined;

  const originAddressId: string | null | undefined =
    quoteParameters.originCustomerAddressId;
  const destinationAddressId: string | null | undefined =
    quoteParameters.destinationCustomerAddressId;

  const [originAddressModel, destinationAddressModel] = await Promise.all([
    originAddressId
      ? fetchAddressBookById(originAddressId)
      : Promise.resolve(undefined),
    destinationAddressId
      ? fetchAddressBookById(destinationAddressId)
      : Promise.resolve(undefined),
  ]);

  const originSnapshot = originAddressModel
    ? addressBookModelToSnapshot(originAddressModel)
    : undefined;
  const destinationSnapshot = destinationAddressModel
    ? addressBookModelToSnapshot(destinationAddressModel)
    : undefined;

  const originMismatch = checkAddressMismatch(
    originAddressModel,
    quoteParameters.originCity,
    quoteParameters.originState,
    quoteParameters.originZip,
  );
  const destinationMismatch = checkAddressMismatch(
    destinationAddressModel,
    quoteParameters.destinationCity,
    quoteParameters.destinationState,
    quoteParameters.destinationZip,
  );

  const finalOriginSnapshot =
    originSnapshot ??
    (address && quoteParameters.originZip === address.zip
      ? defaultAddressToSnapshot(address)
      : undefined);

  const order = buildLoadFromQuote(
    tempQuote,
    address,
    finalOriginSnapshot,
    destinationSnapshot,
  );
  setLTLQuote({
    ltlQuote: createLtlQuoteLegacy(quoteParameters, quote),
    originZip: quoteParameters.originZip,
    originCity: quoteParameters.originCity,
    originState: quoteParameters.originState,
    originCountry: quoteParameters.originCountry,
    destinationZip: quoteParameters.destinationZip,
    destinationCity: quoteParameters.destinationCity,
    destinationState: quoteParameters.destinationState,
    destinationCountry: quoteParameters.destinationCountry,
    customerId: quoteParameters.customerId ?? undefined,
    customerName: quoteParameters.customerName ?? undefined,
    pickupDate: quoteParameters.pickupDate,
    loadItems: mapLoadItems(quoteParameters.loadItems),
    originAddress: finalOriginSnapshot,
    destinationAddress: destinationSnapshot,
    originAddressMismatch: originMismatch,
    destinationAddressMismatch: destinationMismatch,
  });
  //Note: setting the bid type to Spot Market if it is null so creating an order
  //from a saved quote never leaves the required Bid Type field unset.
  updateOrderState({
    ...order,
    bidType: Boolean(orderStore.order.bidType)
      ? orderStore.order.bidType
      : 'Spot Market',
  });
  if (
    quoteParameters.ltlInsuranceQuote &&
    quoteParameters.ltlInsuranceQuote.shippingSumInsured > 0
  ) {
    const { shippingSumInsured, serviceFee, premium } =
      quoteParameters.ltlInsuranceQuote;
    updateCustomerAndVendorPay(
      {
        shippingSumInsured,
        quotePremium: premium,
        quoteServiceFee: serviceFee,
      } as FalveyInsuranceQuoteInLoad,
      'insert',
      shippingSumInsured,
    );
  }
  setOrderLoading(false);
};

// ******************************************************************************
// ↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑
// This block of code between these comments is going away once we remove
// TempQuote
// https://armstrong.atlassian.net/browse/VNEXT-5326
// While this new logic is behind a feature flag the easiest thing to do
// was to cohherce the new data into the old shape.
// ******************************************************************************

export const mergeOrderWithQuote = async (
  id: string,
  existingOrder: IOrderViewModel,
) => {
  await mergeOrderWithNewQuote(id, existingOrder, null);
};

export const mergeOrderWithNewQuote = async (
  id: string,
  existingOrder: IOrderViewModel,
  ltlQuoteId: number | null,
  quoteParametersId?: number,
) => {
  let quote: TempQuoteModel;
  let quoteParams: LtlQuoteParametersViewModel | undefined;
  if (id && id.trim() !== '' && id !== 'undefined') {
    quote = (await getTempQuote(id)) as TempQuoteModel;
    await GetQuoteInfo(quote);
    quote.ltlQuoteId = ltlQuoteId;
  } else {
    quoteParams = await fetchQuoteParametersByQuoteId(
      Number(ltlQuoteId),
      false,
    );
    let loadItems;
    if (Boolean(quoteParametersId)) {
      const quoteLoadItems = await fetchLtlQuoteLoadItems(
        quoteParametersId as number,
      );
      // The quote API may return sub-items as top-level rows linked by
      // parentQuoteLoadItemId. When sub-items are carried onto the load, nest
      // them first so mapLoadItems sees them as breakdowns of their parent
      // rather than as separate load items. Without the flag the rows are
      // used exactly as before.
      loadItems = isFeatureFlagEnabled(
        FeatureFlags.LtlSupportMixedCommodityPalletOnLoad,
      )()
        ? nestLoadItems(quoteLoadItems)
        : quoteLoadItems;
    }
    const Ltlquote = await fetchQuoteById(Number(ltlQuoteId), true);
    if (Ltlquote === null) {
      throw new Error('Failed to fetch LTL quote');
    }
    quote = createTempQuote(
      loadItems ? { ...quoteParams, loadItems } : { ...quoteParams },
      Ltlquote,
    );
  }

  const mergeOriginAddressId: string | null | undefined =
    quoteParams?.originCustomerAddressId;
  const mergeDestAddressId: string | null | undefined =
    quoteParams?.destinationCustomerAddressId;

  const [mergeOriginModel, mergeDestModel] = await Promise.all([
    mergeOriginAddressId
      ? fetchAddressBookById(mergeOriginAddressId)
      : Promise.resolve(undefined),
    mergeDestAddressId
      ? fetchAddressBookById(mergeDestAddressId)
      : Promise.resolve(undefined),
  ]);

  const mergeOriginSnapshot = mergeOriginModel
    ? addressBookModelToSnapshot(mergeOriginModel)
    : undefined;
  const mergeDestSnapshot = mergeDestModel
    ? addressBookModelToSnapshot(mergeDestModel)
    : undefined;

  const mergeOriginMismatch = checkAddressMismatch(
    mergeOriginModel,
    quote.quoteSpecifics.requestDetails.originCity,
    quote.quoteSpecifics.requestDetails.originState,
    quote.quoteSpecifics.requestDetails.originZip,
  );
  const mergeDestMismatch = checkAddressMismatch(
    mergeDestModel,
    quote.quoteSpecifics.requestDetails.destinationCity,
    quote.quoteSpecifics.requestDetails.destinationState,
    quote.quoteSpecifics.requestDetails.destinationZip,
  );

  setLTLQuote({
    ltlQuote: quote.ltlQuote,
    originZip: quote.quoteSpecifics.requestDetails.originZip,
    originCity: quote.quoteSpecifics.requestDetails.originCity,
    originState: quote.quoteSpecifics.requestDetails.originState,
    originCountry: quote.quoteSpecifics.requestDetails.originCountry,
    destinationZip: quote.quoteSpecifics.requestDetails.destinationZip,
    destinationCity: quote.quoteSpecifics.requestDetails.destinationCity,
    destinationState: quote.quoteSpecifics.requestDetails.destinationState,
    destinationCountry: quote.quoteSpecifics.requestDetails.destinationCountry,
    customerId: quote.quoteSpecifics.requestDetails.customerId ?? undefined,
    customerName: quote.quoteSpecifics.requestDetails.customerName ?? undefined,
    pickupDate: quote.quoteSpecifics.requestDetails.pickupDate,
    loadItems: quote.quoteSpecifics.requestDetails.loadItems,
    ltlQuoteId: ltlQuoteId,
    originAddress: mergeOriginSnapshot,
    destinationAddress: mergeDestSnapshot,
    originAddressMismatch: mergeOriginMismatch,
    destinationAddressMismatch: mergeDestMismatch,
  });

  const mergedOrder = mergeOrderQuote(
    quote,
    existingOrder,
    mergeOriginSnapshot,
    mergeDestSnapshot,
  );
  if (Boolean(quote.ltlQuote.ltlInsurance?.shippingSumInsured)) {
    await getLTLFalveyPricingQuotes(
      {
        declaredValue: quote.ltlQuote.ltlInsurance?.shippingSumInsured,
      },
      undefined,
      CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsInsurance,
    );
  }

  setOrderStore('quoteIdUrl', id);
  setOrderStore('quoteId', String(ltlQuoteId));
  updateOrderState(mergedOrder);

  // Populate carrierStore with carrier details from the selected quote
  const carrierId = Number(quote.quoteSpecifics.quoteResponse.carrierId);
  if (carrierId) {
    await getCarrierDetail(carrierId, orderStore.activeTab.index);
  }

  if (
    quote.ltlQuote.ltlInsurance &&
    quote.ltlQuote.ltlInsurance.shippingSumInsured > 0
  ) {
    const { shippingSumInsured, quoteServiceFee, quotePremium } =
      quote.ltlQuote.ltlInsurance;
    addCustomerPayLineItem({
      shippingSumInsured,
      quotePremium,
      quoteServiceFee,
    });
    const falveyVendorIndex = (orderStore.order.vendors ?? []).findIndex(
      (v) => v.name === 'Falvey Shippers Insurance',
    );
    if (falveyVendorIndex === -1) {
      addVendorPayLineItem(
        {
          shippingSumInsured,
          quotePremium,
          quoteServiceFee,
        },
        quote.ltlQuote.ltlInsurance.shippingSumInsured,
      );
    } else {
      updateVendorPayLineItem(
        {
          shippingSumInsured,
          quotePremium,
          quoteServiceFee,
        },
        quote.ltlQuote.ltlInsurance.shippingSumInsured,
      );
    }
  }
};
